import { FormContent, PageCanvas } from "@/components/page-canvas";
import { Form, Head, Link, usePage } from "@inertiajs/react";
import { useTranslation } from "react-i18next";
import AppLayout from "@/layouts/app_layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import CaseWorkspace, { CaseFacts } from "@/components/case-workspace";
import { FormErrors, errorText } from "@/components/forms/form-errors";
import { useResourceDraft } from "@/hooks/use-resource-draft";
import {
  locationDetailingProductPath,
  locationDetailingProductsPath,
  locationDetailingProductStockAdjustmentsPath,
} from "@/routes";
import { quantity, type Product } from "./types";

export default function AdjustmentForm({
  product,
  kind,
}: {
  product: Product;
  kind: "receipt" | "correction" | "threshold";
}) {
  const { auth, errors } = usePage().props;
  const { t, i18n } = useTranslation();
  const draft = useResourceDraft(
    {
      account: auth.account?.id || "",
      actor: auth.user?.id || "",
      location: auth.location?.id || "",
      kind: "stock",
      record: `${product.id}:${kind}`,
      // A receipt adds an observed amount. Counts and thresholds replace a
      // displayed balance/settings value, so only those drafts need its revision.
      revision: kind === "receipt" ? "receipt" : product.balance_revision,
    },
    {
      quantity:
        kind === "correction"
          ? product.quantity
          : kind === "threshold"
            ? product.threshold || ""
            : "",
      reason: "",
    },
  );
  if (!auth.location) return null;
  const locationId = auth.location.id;
  const title = t(
    kind === "threshold" ? "inventory.threshold_action" : `inventory.${kind}`,
  );
  return (
    <AppLayout
      breadcrumbs={[
        {
          title: t("inventory.title"),
          href: locationDetailingProductsPath(locationId),
        },
        {
          title: product.name,
          href: locationDetailingProductPath(locationId, product.id),
        },
      ]}
    >
      <Head title={title} />
      <PageCanvas>
        <CaseWorkspace
          context={
            <CaseFacts
              facts={[
                { label: t("inventory.name"), value: product.name },
                {
                  label: t("resource_workspace.current_balance"),
                  value: `${quantity(product.quantity, i18n.language)} ${t(`inventory.${product.unit}`)}`,
                },
                {
                  label: t("resource_workspace.location"),
                  value: auth.location.name,
                },
              ]}
            />
          }
        >
          <FormContent>
            <h1 className="text-page font-display">{title}</h1>
            <p className="text-muted-foreground text-sm">
              {t(`inventory.${kind}_help`)}
            </p>
            <FormErrors
              errors={errors || {}}
              targets={{ quantity: "stock-quantity", reason: "stock-reason" }}
            />
            {draft.status && (
              <p role="status" className="text-sm text-muted-foreground">
                {t(`resource_drafts.${draft.status}`)}
              </p>
            )}
            <Form
              key={`${kind}:${product.balance_revision}`}
              onSuccess={draft.discard}
              action={locationDetailingProductStockAdjustmentsPath(
                locationId,
                product.id,
              )}
              method="post"
            >
              {({ processing }) => (
                <div className="space-y-4">
                  <input
                    type="hidden"
                    name="revision"
                    value={product.balance_revision}
                  />
                  <input
                    type="hidden"
                    name="stock_adjustment[kind]"
                    value={kind}
                  />
                  <div className="space-y-2">
                    <Label htmlFor="stock-quantity">
                      {t(
                        kind === "threshold"
                          ? "inventory.threshold"
                          : kind === "correction"
                            ? "inventory.count"
                            : "inventory.quantity",
                      )}{" "}
                      ({t(`inventory.${product.unit}`)})
                    </Label>
                    <Input
                      autoFocus
                      id="stock-quantity"
                      name="stock_adjustment[quantity]"
                      inputMode={
                        product.unit === "litre" ? "decimal" : "numeric"
                      }
                      value={draft.values.quantity}
                      onChange={(event) =>
                        draft.change("quantity", event.target.value)
                      }
                      required={kind !== "threshold"}
                      aria-invalid={!!errors?.quantity}
                      aria-describedby={
                        errors?.quantity
                          ? "quantity-help stock-quantity-error"
                          : "quantity-help"
                      }
                    />
                    {errors?.quantity && (
                      <p
                        id="stock-quantity-error"
                        className="text-sm text-destructive"
                      >
                        {errorText(errors.quantity)}
                      </p>
                    )}
                    <p
                      id="quantity-help"
                      className="text-muted-foreground text-sm"
                    >
                      {t(`inventory.precision_${product.unit}`)}
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="stock-reason">
                      {t("inventory.reason")}
                    </Label>
                    <Textarea
                      id="stock-reason"
                      name="stock_adjustment[reason]"
                      value={draft.values.reason}
                      onChange={(event) =>
                        draft.change("reason", event.target.value)
                      }
                      required
                      maxLength={500}
                      aria-invalid={!!errors?.reason}
                      aria-describedby={
                        errors?.reason
                          ? "reason-help stock-reason-error"
                          : "reason-help"
                      }
                    />
                    {errors?.reason && (
                      <p
                        id="stock-reason-error"
                        className="text-sm text-destructive"
                      >
                        {errorText(errors.reason)}
                      </p>
                    )}
                    <p
                      id="reason-help"
                      className="text-muted-foreground text-sm"
                    >
                      {t("inventory.reason_help")}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-3">
                    <Button type="submit" disabled={processing}>
                      {t("inventory.save")}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={draft.discard}
                      render={
                        <Link
                          href={locationDetailingProductPath(
                            locationId,
                            product.id,
                          )}
                        />
                      }
                    >
                      {t("inventory.cancel")}
                    </Button>
                  </div>
                </div>
              )}
            </Form>
          </FormContent>
        </CaseWorkspace>
      </PageCanvas>
    </AppLayout>
  );
}
